import { useMutation } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { z } from "zod";
import type { SkillCreate, SkillResponse } from "@/api";
import { createSkillMutation, getSkillQueryKey, listSkillsQueryKey, schemas, updateSkillMutation } from "@/api";
import { EntityFormCard, formText, IdentityFields } from "@/components/build/EntityForm";
import { useEntitySave } from "@/components/build/useEntitySave";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";

// Form rules from the GENERATED schemas. SkillUpdate is PATCH-shaped; the
// form edits the subset below (identity + content). Visibility/access lives
// on the Access tab, not here.
const update = schemas.zSkillUpdate.shape;
const skillFormSchema = z.object({
  name: update.name.unwrap().unwrap(),
  description: update.description.unwrap().unwrap(),
  instructions: update.instructions.unwrap().unwrap(),
  category: update.category,
});
type SkillFormValues = z.infer<typeof skillFormSchema>;

/** A Build with AI draft opened in the create form ("Open in form" on its card). */
export interface SkillFormState {
  draft: Partial<SkillCreate>;
  /** The chat the draft came from; the created skill is linked to it. */
  sourceSessionUuid?: string;
}

/** The skill Overview form — the create page and the detail tab share it. Built-in (system) skills, and skills
 * the user can't edit, are shown read only: nobody can change a built-in skill (the API refuses). */
export function SkillForm({ skill }: Readonly<{ skill?: SkillResponse }>) {
  const { t } = useTranslation("skills");
  const navigate = useNavigate();
  const fromChat = (useLocation().state as SkillFormState | null) ?? undefined;
  const seed = skill ? undefined : fromChat?.draft;
  const text = formText(t, t);
  const { saveExisting, createNew } = useEntitySave(text.messages);
  const readOnly = Boolean(skill && (skill.isSystem || !skill.canEdit));

  const create = useMutation(createSkillMutation());
  const updateMutation = useMutation(updateSkillMutation());

  const form = useAppForm({
    defaultValues: {
      name: skill?.name ?? seed?.name ?? "",
      description: skill?.description ?? seed?.description ?? "",
      instructions: skill?.instructions ?? seed?.instructions ?? "",
      category: skill?.category ?? seed?.category ?? "",
    } as SkillFormValues,
    validators: { onChange: skillFormSchema },
    onSubmit: ({ value }) => {
      const base = {
        name: value.name,
        description: value.description,
        instructions: value.instructions,
        category: value.category || null,
      };
      if (skill) {
        return saveExisting({
          request: () =>
            updateMutation.mutateAsync({ path: { skill_uuid: skill.uuid }, body: base, throwOnError: true }),
          invalidate: [listSkillsQueryKey(), getSkillQueryKey({ path: { skill_uuid: skill.uuid } })],
          onSaved: () => form.reset(value),
        });
      }
      return createNew({
        request: async () =>
          (
            await create.mutateAsync({
              body: { ...base, sourceSessionUuid: fromChat?.sourceSessionUuid },
              throwOnError: true,
            })
          ).data.uuid,
        invalidate: [listSkillsQueryKey()],
        to: (uuid) => `/build/skills/${uuid}`,
      });
    },
  });

  return (
    <EntityFormCard
      label={t("form.label")}
      onSubmit={() => void form.handleSubmit()}
      readOnly={readOnly}
      notice={readOnly && <ReadOnlyNotice text={skill?.isSystem ? t("readOnly.system") : t("readOnly.viewOnly")} />}
    >
      <IdentityFields
        form={form}
        fields={{ name: "name", description: "description" }}
        labels={text.labels}
        descriptionRequired
      />
      <form.AppField name="instructions">
        {(field) => (
          <field.TextareaField
            label={t("form.instructions")}
            required
            placeholder={t("form.instructionsPlaceholder")}
            rows={10}
          />
        )}
      </form.AppField>
      <form.AppField name="category">
        {(field) => <field.TextField label={t("form.category")} placeholder={t("form.categoryPlaceholder")} />}
      </form.AppField>
      {!readOnly && (
        <form.AppForm>
          <form.SubmitActions
            onCancel={() => (skill ? form.reset() : navigate("/build/skills"))}
            submitLabel={skill ? undefined : t("form.create")}
            requireChanges={Boolean(skill)}
          />
        </form.AppForm>
      )}
    </EntityFormCard>
  );
}

function ReadOnlyNotice({ text }: Readonly<{ text: string }>) {
  return (
    <div className="flex items-center gap-2 text-content-secondary">
      <Lock size={16} aria-hidden />
      <Text variant="body-sm" tone="secondary">
        {text}
      </Text>
    </div>
  );
}
